<template>
    <div class="cms-container">
        <ToastCMS ref="toast" />

        <!-- IMAGE PREVIEW MODAL -->
        <div v-if="showPreviewModal" class="preview-modal w-100 h-100 d-flex align-items-center justify-content-center"
            @click.self="closePreviewImage">
            <div class="d-block align-items-center justify-content-center w-100 mx-3">
                <div class="modal-content bg-transparent position-relative border-0">
                    <button class="close-btn position-fixed text-white bg-transparent border-0"
                        @click="closePreviewImage">&times;</button>
                    <div class="d-flex justify-content-between align-items-center border-0">
                        <img :src="previewImages[currentPreviewIndex]" alt="Preview Image">
                    </div>
                </div>
                <div v-if="previewImages.length > 1" class="text-center mt-3">
                    <span class="text-white">Image {{ currentPreviewIndex + 1 }} of {{ previewImages.length }}</span>
                </div>
            </div>
        </div>

        <div class="form-container">
            <form id="keyvisuals" @submit.prevent="saveAllChanges">

                <!-- KEY VISUAL SLIDE CAPTIONS -->
                <div class="vmc-main-content">
                    <div class="sticky-controls">
                        <div class="kv-wrapper d-flex justify-content-between align-items-center px-3">
                            <div class="text-dark key-visual-header">Key Visual Slides</div>
                        </div>
                    </div>
                    <div>
                        <div class="grid-header">
                            <div class="w-100 mt-4" v-for="n in 6" :key="'caption-' + n">
                                <div class="px-3">
                                    <div class="d-flex justify-content-between">
                                        <h3 class="kv-slide-no text-dark">Slide # {{ n }}</h3>
                                        <button type="button" @click="clearText(n)"
                                            class="vmc-bg-prime-4 border-0 px-2 text-white rounded">
                                            Clear Slide
                                        </button>
                                    </div>

                                    <!-- TOP CAPTION — maps to captionOneP${n} in admin CMS -->
                                    <div class="input-group mt-3">
                                        <input type="text" class="input-field text-dark p-2" placeholder=""
                                            v-model="form[`captionOneP${n}`]">
                                        <label class="floating-label">Top Caption</label>
                                    </div>

                                    <!-- MAIN TITLE — maps to captionTwoP${n} in admin CMS -->
                                    <div class="input-group mt-3">
                                        <input type="text" class="input-field text-dark p-2" placeholder=""
                                            v-model="form[`captionTwoP${n}`]">
                                        <label class="floating-label">Main Title</label>
                                    </div>

                                    <!-- DESCRIPTION — maps to captionThreeP${n} in admin CMS -->
                                    <div class="form-group mt-3">
                                        <label class="text-dark">Description:</label><br>
                                        <textarea rows="3" placeholder="" style="outline:none; resize:none;"
                                            class="form-input w-100 px-1 rounded text-dark"
                                            v-model="form[`captionThreeP${n}`]"></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- HOME PAGE SLIDER IMAGES -->
                <div class="sticky-controls">
                    <div class="slider-header px-3 text-dark">Image Slider (Key Visuals)</div>
                </div>
                <div>
                    <div class="grid-header">
                        <div class="w-100 mt-4" v-for="(key, index) in slideKeys" :key="'slide-' + index">
                            <div class="px-3 py-2">
                                <div class="d-flex align-items-center justify-content-between mb-2" style="gap:5px;">
                                    <span class="img-slide-header text-dark">Slide # {{ index + 1 }}</span>
                                    <button type="button" @click="previewImage(key)"
                                        class="vmc-bg-prime-4 text-white rounded px-3 py-1 border-0">
                                        View
                                    </button>
                                </div>

                                <!-- IMAGE DISPLAY AREA — maps to form.keyVisuals[key] in admin CMS -->
                                <div
                                    style="height:200px; width:100%; background-position:center; border:1px solid gray; margin-bottom:10px; display:flex; justify-content:center; align-items:center; overflow:hidden;">
                                    <img v-if="form.keyVisuals && form.keyVisuals[key]" :src="form.keyVisuals[key]"
                                        alt="Slide Image" style="height:100%; width:100%; object-fit:contain;">

                                    <template v-else>
                                        <input type="file" :id="'upload-keyvisual-img-' + key"
                                            accept="image/png, image/jpeg, image/jpg" hidden
                                            @change="onImageChange($event, key)">
                                        <label :for="'upload-keyvisual-img-' + key"
                                            class="w-100 h-100 d-flex flex-column justify-content-center align-items-center text-center"
                                            style="cursor:pointer;">
                                            <i class="material-symbols-outlined">upload</i>
                                            <span>Upload Photo</span>
                                        </label>
                                    </template>
                                </div>

                                <button @click="resetImage(key)" type="button"
                                    class="w-100 vmc-bg-prime-6 btn-sm text-white mt-1 border-0 rounded">
                                    Remove Photo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <div class="px-3 my-4">
                <button type="submit" form="keyvisuals"
                    class="vmc-bg-prime-4 border-0 px-3 py-1 text-white mx-1 rounded">
                    Save All Changes
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import ToastCMS from '../ToastCMS.vue';

/* Matches the SLIDE_KEYS constant in the admin CMS */
const SLIDE_KEYS = ['slide1', 'slide2', 'slide3', 'slide4', 'slide5', 'slide6'];

export default {
    name: "KeyVisualsCMS",
    components: { ToastCMS },

    data() {
        return {
            slideKeys: SLIDE_KEYS,

            /*
             * form mirrors the admin CMS shape:
             *   captionOneP1..6   → Top Caption
             *   captionTwoP1..6   → Main Title
             *   captionThreeP1..6 → Description
             *   keyVisuals        → { slide1:'', slide2:'', ... slide6:'' }
             */
            form: this.initializeForm(),

            /* Snapshots used by hasChanges to detect unsaved edits */
            originalForm: {},

            /* Preview modal state */
            previewImages: [],
            showPreviewModal: false,
            currentPreviewIndex: 0,
        };
    },

    computed: {
        /**
         * Mirrors the admin CMS hasChanges guard.
         * Blocks saveAllChanges when nothing has actually been edited.
         */
        hasChanges() {
            return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
        }
    },

    methods: {
        /* ── INIT ─────────────────────────────────────────────────── */

        initializeForm() {
            const slideTexts = {};
            for (let i = 1; i <= 6; i++) {
                slideTexts[`captionOneP${i}`] = "";
                slideTexts[`captionTwoP${i}`] = "";
                slideTexts[`captionThreeP${i}`] = "";
            }
            return {
                ...slideTexts,
                keyVisuals: SLIDE_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {})
            };
        },

        /* ── LOAD ─────────────────────────────────────────────────── */

        /**
         * Fetches saved content from the backend API (same endpoint the
         * admin CMS uses) and merges only the Key Visuals fields into form.
         */
        async loadFormData() {
            try {
                const token = localStorage.getItem('vmc_token');
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/content?_nocache=${new Date().getTime()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error("Failed to fetch content");

                const data = await response.json();

                if (data && Object.keys(data).length > 0) {
                    /* Merge caption fields */
                    for (let i = 1; i <= 6; i++) {
                        if (data[`captionOneP${i}`] !== undefined) this.form[`captionOneP${i}`] = data[`captionOneP${i}`];
                        if (data[`captionTwoP${i}`] !== undefined) this.form[`captionTwoP${i}`] = data[`captionTwoP${i}`];
                        if (data[`captionThreeP${i}`] !== undefined) this.form[`captionThreeP${i}`] = data[`captionThreeP${i}`];
                    }

                    /* Merge keyVisuals object */
                    if (data.keyVisuals) {
                        this.form.keyVisuals = { ...this.form.keyVisuals, ...data.keyVisuals };
                    }
                }

                /* Snapshot for hasChanges comparison */
                this.originalForm = JSON.parse(JSON.stringify(this.form));

            } catch (error) {
                console.error("Load error:", error);
                this.$refs.toast.showToast("Failed to load data!", "error");
            }
        },

        /* ── CAPTIONS ─────────────────────────────────────────────── */

        /**
         * Clears caption fields for a given slide.
         * Shows a warning toast if the slide was already empty.
         */
        clearText(n) {
            const isEmpty =
                (!this.form[`captionOneP${n}`] || this.form[`captionOneP${n}`].trim() === "") &&
                (!this.form[`captionTwoP${n}`] || this.form[`captionTwoP${n}`].trim() === "") &&
                (!this.form[`captionThreeP${n}`] || this.form[`captionThreeP${n}`].trim() === "");

            this.form[`captionOneP${n}`] = "";
            this.form[`captionTwoP${n}`] = "";
            this.form[`captionThreeP${n}`] = "";
            this.$forceUpdate();

            if (isEmpty) {
                this.$refs.toast.showToast(`Slide # ${n} has no input`, "warning");
            } else {
                this.$refs.toast.showToast(`Slide # ${n} text cleared`, "success");
            }
        },

        /* ── IMAGES ───────────────────────────────────────────────── */

        /**
         * Reads the uploaded file as base64 and stores it in
         * form.keyVisuals[key] — matching the admin CMS handleUpload pattern.
         */
        async onImageChange(e, key) {
            const file = e.target.files[0];
            if (file && file.type.startsWith("image/")) {
                const formData = new FormData();
                formData.append('image', file);

                try {
                    // Remove the domain and just use the relative path
                    const response = await fetch('/vmc/api/upload', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('vmc_token')}`
                        },
                        body: formData
                    });

                    const data = await response.json();

                    if (response.ok) {
                        this.$set(this.form.keyVisuals, key, data.url);
                        const slideNum = SLIDE_KEYS.indexOf(key) + 1;
                        this.$refs.toast.showToast(`Slide # ${slideNum} image attached`, "success");
                    } else {
                        console.error("Upload failed:", data.message);
                    }
                } catch (error) {
                    console.error("Upload error:", error);
                }
            }
            e.target.value = ""; // reset so same file can be re-selected
        },

        /**
         * Clears the image for the given slideKey.
         * Mirrors admin CMS: this.$set(form.keyVisuals, key, '')
         */
        resetImage(key) {
            const slideNum = SLIDE_KEYS.indexOf(key) + 1;

            if (!this.form.keyVisuals[key]) {
                this.$refs.toast.showToast(`No image attached to Slide # ${slideNum}`, "warning");
                return;
            }

            this.$set(this.form.keyVisuals, key, "");
            this.$refs.toast.showToast(`Slide # ${slideNum} image removed`, "error");
        },

        /* ── PREVIEW ──────────────────────────────────────────────── */

        previewImage(key) {
            if (!this.form.keyVisuals[key]) {
                this.$refs.toast.showToast("No image to view", "warning");
                return;
            }
            this.previewImages = [this.form.keyVisuals[key]];
            this.currentPreviewIndex = 0;
            this.showPreviewModal = true;
        },

        closePreviewImage() {
            this.showPreviewModal = false;
            this.previewImages = [];
        },

        onKeyboard(e) {
            if (this.showPreviewModal && e.key === 'Escape') {
                this.showPreviewModal = false;
            }
        },

        /* ── SAVE ─────────────────────────────────────────────────── */

        /**
         * Posts only the Key Visuals fields to the same backend endpoint
         * the admin CMS uses (POST /api/update), with JWT auth.
         * Guards against saving when nothing has changed (hasChanges).
         */
        async saveAllChanges(e) {
            e.preventDefault();

            if (!this.hasChanges) {
                this.$refs.toast.showToast("Nothing to save", "warning");
                return;
            }

            try {
                /* Build a payload containing only Key Visuals fields */
                const payload = { keyVisuals: this.form.keyVisuals };
                for (let i = 1; i <= 6; i++) {
                    payload[`captionOneP${i}`] = this.form[`captionOneP${i}`];
                    payload[`captionTwoP${i}`] = this.form[`captionTwoP${i}`];
                    payload[`captionThreeP${i}`] = this.form[`captionThreeP${i}`];
                }

                const token = localStorage.getItem('vmc_token');
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/update`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error("Server error");

                /* Update snapshot so hasChanges resets to false */
                this.originalForm = JSON.parse(JSON.stringify(this.form));

                this.$refs.toast.showToast("Saved all changes.", "success");

                setTimeout(() => { window.location.reload(); }, 1500);

            } catch (error) {
                console.error("Save error:", error);
                this.$refs.toast.showToast("Save failed. Check your connection.", "error");
            }
        }
    },

    async mounted() {
        window.addEventListener('keydown', this.onKeyboard);
        await this.loadFormData();
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeyboard);
    }
};
</script>

<style scoped>
.toast-container {
    position: fixed;
    width: 300px;
    top: 20px;
    right: 20px;
    z-index: 9999;
}

hr {
    border-top: 1px solid black;
}

.sticky-controls {
    position: sticky;
    z-index: 1;
    background-color: white;
}

@media (min-width: 330px) {
    .kv-wrapper {
        margin-top: 1px;
    }

    .key-visual-header {
        font-size: 17px;
        font-weight: bold;
    }

    .kv-slide-no {
        font-size: 18px;
    }

    .slider-header,
    .img-slide-header {
        font-weight: 600;
    }

    .slider-header {
        font-size: 17px;
    }

    .kv-wrapper,
    .slider-header {
        padding: 15px 0;
        border-bottom: 1px solid #ccc;
        border-top: 1px solid #ccc;
    }

    .image-slide-header {
        font-size: 18px;
    }

    .input-group {
        position: relative;
        display: flex;
    }

    .input-field {
        border: 1px solid gray;
        border-radius: 4px !important;
        width: 100%;
        outline: none;
    }

    input {
        height: 36px;
    }

    input textarea {
        font-size: 15px;
    }

    .floating-label {
        position: absolute;
        top: 50%;
        left: 8px;
        transform: translateY(-50%);
        color: gray;
        background-color: white;
        transition: all 0.3s ease;
        pointer-events: none;
    }

    .input-field:focus~.floating-label,
    .input-field:not(:placeholder-shown)~.floating-label {
        top: 0;
        left: 8px;
        transform: translateY(-50%);
        font-size: 12px;
    }

    button {
        font-size: 16px;
    }

    .grid-header>div {
        width: 100%;
    }

    .preview-modal {
        position: fixed;
        top: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.8);
        z-index: 9999;
        overflow-y: hidden;
        overscroll-behavior: contain;
    }

    .modal-content img {
        max-width: 70vw;
        max-height: 70vh;
        width: auto;
        height: auto;
        object-fit: contain;
        display: block;
        margin: 0 auto;
    }

    .close-btn {
        top: 10px;
        right: 15px;
        font-size: 24px;
        cursor: pointer;
    }

    .sticky-controls {
        top: 60px;
    }
}

@media (min-width: 768px) {

    .key-visual-header,
    .slider-header {
        font-size: 20px;
    }
}

@media (min-width: 992px) {
    .grid-header {
        display: flex;
        flex-wrap: wrap;
    }

    .grid-header>div {
        width: 50% !important;
        box-sizing: border-box;
    }

    .modal-content img {
        max-height: 80vh;
    }
}

@media (min-width: 1200px) {
    .cms-container {
        margin-left: 310px;
        margin-top: 82px;
    }

    .sticky-controls {
        top: 82px;
    }
}
</style>